/**
 * Hook de récupération de mot de passe v1.0
 * Architecture Frontend-First avec appels directs à l'IAM
 * 
 * @version 2.7.9
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { mobilePasswordService } from '../services/mobilePassword';
import { setNativeAuthConfig, ApiError, repairNativeSsoStorage, isDebugMode } from '../services/api';
import type { MobilePasswordState } from '../types/mobile';

export interface UseMobilePasswordOptions {
  saasApiUrl: string;
  iamApiUrl: string;
  autoLoadCredentials?: boolean;
  
}

interface MobilePasswordStateExtended extends MobilePasswordState {
  credentialsLoaded: boolean;
  errorType: string | null;
}

function getErrorMessage(err: unknown, context: string): { message: string; type: string } {
  if (err instanceof ApiError) {
    return { message: err.message, type: err.type };
  }
  if (err instanceof Error) {
    if (err.message.includes('fetch') || err.message.includes('network')) {
      return { message: 'Vérifiez votre connexion Internet', type: 'network' };
    }
    if (err.message.includes('timeout')) {
      return { message: 'Le serveur met trop de temps à répondre', type: 'timeout' };
    }
    return { message: err.message, type: 'unknown' };
  }
  return { message: `Erreur lors de ${context}`, type: 'unknown' };
}

export function useMobilePassword(options: UseMobilePasswordOptions) {
  const { saasApiUrl, iamApiUrl, autoLoadCredentials = true } = options;

  const configuredRef = useRef(false);

  useEffect(() => {
    const repairResult = repairNativeSsoStorage();
    if (repairResult.cleaned && isDebugMode()) {
      console.warn('🔧 [useMobilePassword] Storage SSO réparé', repairResult.reason);
    }
  }, []);

  useEffect(() => {
    if (!configuredRef.current) {
      setNativeAuthConfig({ saasApiUrl, iamApiUrl });
      configuredRef.current = true;
    }
  }, [saasApiUrl, iamApiUrl]);

  const [state, setState] = useState<MobilePasswordStateExtended>({
    credentialsLoaded: false,
    processToken: null,
    status: 'idle',
    storedOtp: null,
    receiveMode: null,
    maskedEmail: null,
    maskedPhone: null,
    loading: false,
    error: null,
    errorType: null,
  });

  const loadCredentials = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null, errorType: null }));
    try {
      await mobilePasswordService.loadCredentials();
      setState(prev => ({ ...prev, credentialsLoaded: true, loading: false }));
      return { success: true };
    } catch (err) {
      const { message, type } = getErrorMessage(err, 'le chargement');
      setState(prev => ({ ...prev, loading: false, error: message, errorType: type }));
      return { success: false, error_type: type };
    }
  }, []);

  useEffect(() => {
    if (autoLoadCredentials && !state.credentialsLoaded) {
      loadCredentials();
    }
  }, [autoLoadCredentials, state.credentialsLoaded, loadCredentials]);

  const initRecovery = useCallback(async (email: string) => {
    setState(prev => ({ ...prev, loading: true, error: null, errorType: null }));
    try {
      if (!mobilePasswordService.hasCredentials()) {
        await mobilePasswordService.loadCredentials();
      }
      const response = await mobilePasswordService.initRecovery(email);

      if (response.success && response.process_token) {
        if (response.status === 'choice_required') {
          setState(prev => ({
            ...prev,
            credentialsLoaded: true,
            processToken: response.process_token!,
            status: 'choice_required',
            maskedEmail: response.masked_email || null,
            maskedPhone: response.masked_phone || null,
            loading: false,
          }));
          return { success: true, status: 'choice_required' as const };
        } else {
          setState(prev => ({
            ...prev,
            credentialsLoaded: true,
            processToken: response.process_token!,
            status: 'pending_otp',
            storedOtp: response.otp_code || null,
            receiveMode: 'email',
            maskedEmail: response.masked_email || null,
            loading: false,
          }));
          return { success: true, status: 'pending_otp' as const };
        }
      }

      setState(prev => ({
        ...prev,
        loading: false,
        error: response.message || 'Aucun compte trouvé avec cet email',
        errorType: response.error_type || 'unknown',
      }));
      return { success: false, error_type: response.error_type };
    } catch (err) {
      const { message, type } = getErrorMessage(err, 'la recherche du compte');
      setState(prev => ({ ...prev, loading: false, error: message, errorType: type }));
      return { success: false, error_type: type };
    }
  }, []);

  const selectMethod = useCallback(async (choice: 'email' | 'phone') => {
    if (!state.processToken) {
      return { success: false, error: 'Session invalide' };
    }
    setState(prev => ({ ...prev, loading: true, error: null, errorType: null }));
    try {
      const response = await mobilePasswordService.selectMethod(state.processToken, choice);
      if (response.success) {
        setState(prev => ({
          ...prev,
          status: 'pending_otp',
          storedOtp: response.otp_code || null,
          receiveMode: response.receive_mode || choice,
          loading: false,
        }));
        return { success: true };
      }
      setState(prev => ({
        ...prev,
        loading: false,
        error: response.message || 'Impossible d\'envoyer le code',
        errorType: response.error_type || 'unknown',
      }));
      return { success: false, error_type: response.error_type };
    } catch (err) {
      const { message, type } = getErrorMessage(err, 'l\'envoi du code');
      setState(prev => ({ ...prev, loading: false, error: message, errorType: type }));
      return { success: false, error_type: type };
    }
  }, [state.processToken]);

  const verifyOtp = useCallback((inputCode: string): boolean => {
    if (!state.storedOtp) {
      setState(prev => ({ ...prev, error: 'Session invalide', errorType: 'invalid_session' }));
      return false;
    }
    const isValid = mobilePasswordService.validateOtpLocally(inputCode, state.storedOtp);
    if (isValid) {
      setState(prev => ({ ...prev, status: 'pending_password', error: null, errorType: null }));
      return true;
    }
    setState(prev => ({ ...prev, error: 'Code incorrect', errorType: 'invalid_otp' }));
    return false;
  }, [state.storedOtp]);

  const resetPassword = useCallback(async (password: string) => {
    if (!state.processToken) {
      return { success: false, error: 'Session invalide' };
    }
    setState(prev => ({ ...prev, loading: true, error: null, errorType: null }));
    try {
      const response = await mobilePasswordService.reset(state.processToken, password);
      if (response.success) {
        setState(prev => ({ ...prev, status: 'completed', loading: false }));
        return { success: true };
      }
      setState(prev => ({
        ...prev,
        loading: false,
        error: response.message || 'Erreur lors de la réinitialisation',
        errorType: response.error_type || 'unknown',
      }));
      return { success: false, error_type: response.error_type };
    } catch (err) {
      const { message, type } = getErrorMessage(err, 'la réinitialisation');
      setState(prev => ({ ...prev, loading: false, error: message, errorType: type }));
      return { success: false, error_type: type };
    }
  }, [state.processToken]);

  const resendOtp = useCallback(async () => {
    if (!state.processToken) {
      return { success: false, error: 'Session invalide' };
    }
    setState(prev => ({ ...prev, loading: true, error: null, errorType: null }));
    try {
      const response = await mobilePasswordService.resendOtp(state.processToken);
      if (response.success && response.otp_code) {
        setState(prev => ({ ...prev, storedOtp: response.otp_code!, loading: false }));
        return { success: true, cooldown: response.cooldown_remaining };
      }
      setState(prev => ({ ...prev, loading: false }));
      return { success: false, error_type: response.error_type };
    } catch (err) {
      const { message, type } = getErrorMessage(err, 'le renvoi');
      setState(prev => ({ ...prev, loading: false, error: message, errorType: type }));
      return { success: false, error_type: type };
    }
  }, [state.processToken]);

  const reset = useCallback(() => {
    setState({
      credentialsLoaded: state.credentialsLoaded,
      processToken: null,
      status: 'idle',
      storedOtp: null,
      receiveMode: null,
      maskedEmail: null,
      maskedPhone: null,
      loading: false,
      error: null,
      errorType: null,
    });
  }, [state.credentialsLoaded]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null, errorType: null }));
  }, []);

  return {
    credentialsLoaded: state.credentialsLoaded,
    processToken: state.processToken,
    status: state.status,
    storedOtp: state.storedOtp,
    receiveMode: state.receiveMode,
    maskedEmail: state.maskedEmail,
    maskedPhone: state.maskedPhone,
    loading: state.loading,
    error: state.error,
    errorType: state.errorType,

    isCompleted: state.status === 'completed',
    isChoiceRequired: state.status === 'choice_required',
    isPendingOtp: state.status === 'pending_otp',
    isPendingPassword: state.status === 'pending_password',

    loadCredentials,
    initRecovery,
    selectMethod,
    verifyOtp,
    resetPassword,
    resendOtp,
    reset,
    clearError,
  };
}

export default useMobilePassword;
